import { TrendingUp, CheckCircle2, Clock } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, Skeleton } from '@/components/ui'
import { useWeekTimeLogSummary, useMyTasks } from '@/hooks'
import { cn } from '@/lib/utils'

function getMilestone(hours: number): string {
  if (hours >= 40) return 'Extra mile'
  if (hours >= 30) return 'Strong week'
  if (hours >= 20) return 'Good pace'
  return 'Getting started'
}

function getMilestoneColor(hours: number): string {
  if (hours >= 40) return 'text-emerald-600 dark:text-emerald-400'
  if (hours >= 30) return 'text-primary'
  if (hours >= 20) return 'text-blue-500'
  return 'text-muted-foreground'
}

export function MyWeekProgress() {
  const { data: timeSummary, isLoading: timeLoading } = useWeekTimeLogSummary()
  const { data: taskData, isLoading: tasksLoading } = useMyTasks()

  const isLoading = timeLoading || tasksLoading

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            My Week
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[80px] rounded-lg" />
        </CardContent>
      </Card>
    )
  }

  const hoursLogged = timeSummary?.total_hours ?? 0
  const completedThisWeek = (taskData?.items ?? []).filter(
    (t) => t.status === 'completed'
  ).length
  const progressPercent = Math.min((hoursLogged / 40) * 100, 100)
  const milestone = getMilestone(hoursLogged)
  const milestoneColor = getMilestoneColor(hoursLogged)

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          My Week
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            {hoursLogged.toFixed(1)}h logged
          </span>
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {completedThisWeek} completed
          </span>
        </div>

        {/* Progress bar */}
        <div className="space-y-1">
          <div className="h-2.5 rounded-full bg-muted/40 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-primary to-[hsl(280,100%,70%)] transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className={cn('text-xs font-medium', milestoneColor)}>
              {milestone}
            </span>
            <span className="text-xs text-muted-foreground">
              {Math.round(progressPercent)}% of 40h
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
